import { Button, Intent } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import {
  ButtonContainer,
  ButtonsBloc,
  DataTable,
  FGCustomPanel,
  FGMaskInput,
  FGTextInput,
  FieldGroup,
  FieldSet,
  InlineButtonContainer,
  useGridState,
  useSearchApi
} from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";

import {
  FcbFormateurDoublonGridDto,
  FormateurDoublonApi,
  FormateurDoublonSearch,
  FormateurDoublonSearchFromJSON
} from "../../../../../api";
import { ERoutes } from "../../../../../AppRouter";
import {
  AddButton,
  CancelButton,
  EditButton,
  FGWalterDateMaskInput,
  FGWalterSelectInput,
  SmallFormGenerator,
  UnordoredListColumn,
  ViewButton
} from "../../../../../components";
import { useBeIdCardContext, useDialog } from "../../../../../contexts";
import { useApiService, useTl } from "../../../../../hooks";
import { useReferential } from "../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../locales";

export interface IFormateurSignaletiqueDoublonsProps {
  setBaseData: React.Dispatch<React.SetStateAction<any>>;
}

export const FormateurSignaletiqueDoublons: React.FunctionComponent<IFormateurSignaletiqueDoublonsProps> = ({
  setBaseData
}) => {
  const { t } = useTl();
  const history = useHistory();
  const [nationalites, nLoading] = useReferential(a => a.referentialGetAllNationalite(), false, []);
  const api = useApiService(FormateurDoublonApi);
  const { foundRowsData } = useBeIdCardContext();
  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    enableFilter: false,
    availablePageSizes: [10],
    pageSize: 10,
    sortKeys: { nom: "ASC" }
  });
  const dialogContext = useDialog();

  const searchFunction = React.useCallback(
    (sObj?: FormateurDoublonSearch) => {
      return api.formateurDoublonBaseSearch({ FormateurDoublonSearch: sObj });
    },
    [api]
  );

  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: false
  });

  const initialData = React.useMemo(() => {
    const onlyRegNatNb =
      !foundRowsData ||
      (!foundRowsData.nom && !foundRowsData.prenom && !foundRowsData.dateNaissance && foundRowsData.nationalNb);
    return FormateurDoublonSearchFromJSON({
      nom: foundRowsData?.nom,
      prenom: foundRowsData?.prenom,
      registreNational: onlyRegNatNb ? foundRowsData?.nationalNb : undefined,
      dateNaissance: foundRowsData?.dateNaissance
    });
  }, [foundRowsData]);

  const { totalCount } = tableState;
  const add = React.useCallback(
    (fromPersonne: boolean) => {
      if (totalCount && totalCount > 0) {
        dialogContext.showDialog({
          message: t(fromPersonne ? ETLCodes.AddFormeConfirmMessage : ETLCodes.AddConfirmMessage, {
            forme: t(ETLCodes.Formateur)
          }),
          onConfirmed: async () => {
            history.push(`${ERoutes.formateur}/0/signaletique/edit`);
          }
        });
      } else {
        history.push(`${ERoutes.formateur}/0/signaletique/edit`);
      }
    },
    [dialogContext, history, t, totalCount]
  );

  const columns = React.useMemo(
    () => [
      {
        computed: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: FcbFormateurDoublonGridDto) => (
          <ButtonContainer>
            {row.isFormateur ? (
              <>
                <ViewButton
                  minimal={true}
                  onClick={() => history.push(`${ERoutes.formateur}/${row.idformateur}/signaletique/view`)}
                />
                <EditButton
                  minimal={true}
                  onClick={() => history.push(`${ERoutes.formateur}/${row.idformateur}/signaletique/edit`)}
                />
              </>
            ) : (
              <AddButton
                tooltipText={t(ETLCodes.CreerAPartirdePersonne)}
                onClick={() => {
                  setBaseData({ ...row });
                  add(!row.isFormateur);
                }}
              />
            )}
          </ButtonContainer>
        )
      },
      {
        header: () => t(ETLCodes.Nom),
        fieldName: "nom"
      },
      {
        header: () => t(ETLCodes.Prenom),
        fieldName: "prenom"
      },
      {
        header: () => t(ETLCodes.DateNaissance),
        fieldName: "dateNaissance"
      },
      {
        header: () => t(ETLCodes.NumeroNational),
        fieldName: "registreNational"
      },
      {
        header: () => t(ETLCodes.Adresse),
        fieldName: "adresse"
      },
      {
        header: () => t(ETLCodes.CodePostal),
        fieldName: "codePostal"
      },
      {
        header: () => t(ETLCodes.Localite),
        fieldName: "localite"
      },
      {
        header: () => t(ETLCodes.Nationalite),
        fieldName: "nationalite"
      }
      // {
      //   header: () => t(ETLCodes.Forme),
      //   fieldName: "forme",
      //   render: (row: FcbFormateurDoublonGridDto) => (
      //     <UnordoredListColumn
      //       values={[
      //         { value: row.isApprenant, label: t(ETLCodes.Apprenant) },
      //         { value: row.isFormateur, label: t(ETLCodes.Formateur) },
      //         { value: row.isRepresentant, label: t(ETLCodes.Representant) },
      //         { value: row.isTuteur, label: t(ETLCodes.Tuteur) },
      //         { value: row.isChefEntreprise, label: t(ETLCodes.ChefEntreprise) },
      //         { value: row.isCollaborateur, label: t(ETLCodes.Collaborateur) },
      //         { value: row.isDelegueTutelle, label: t(ETLCodes.Referent) },
      //         { value: row.isConseillePedagogique, label: t(ETLCodes.ConseillerPedagogique) }
      //       ]}
      //     />
      //   )
      // }
    ],
    [add, history, setBaseData, t]
  );
  return (
    <FieldSet collapsable={false} title={t(ETLCodes.Formateurs)}>
      <SmallFormGenerator
        initialValues={initialData}
        onSubmit={c => search(c)}
        editMode
        hideButtons={true}
        onCancel={() => history.push(`${ERoutes.formateur}`)}
        enableDirtyCheck={false}
      >
        <FieldGroup columns={2}>
          <FGTextInput name="nom" label={t(ETLCodes.Nom)} maxLength={50} forceCase="upper" />
          <FGWalterSelectInput
            name="nationalite"
            label={t(ETLCodes.Nationalite)}
            items={nationalites}
            loading={nLoading}
          />
          <FGTextInput name="prenom" label={t(ETLCodes.Prenom)} maxLength={50} />
          <FGMaskInput
            name="registreNational"
            label={t(ETLCodes.NumeroNational)}
            cleaveOptions={{ delimiters: [".", ".", "-", "."], blocks: [2, 2, 2, 3, 2], numericOnly: true }}
            requiredMark
          />
          <FGWalterDateMaskInput name="dateNaissance" label={t(ETLCodes.DateNaissance)} />
        </FieldGroup>
        <FieldGroup>
          <FGCustomPanel>
            {ctx => (
              <InlineButtonContainer>
                <ButtonsBloc></ButtonsBloc>
                <ButtonsBloc>
                  <CancelButton minimal={false} onClick={() => history.push(`${ERoutes.formateur}`)} />
                  <Button
                    icon={IconNames.SEARCH}
                    minimal={false}
                    text={t(ETLCodes.Search)}
                    intent={Intent.PRIMARY}
                    type="submit"
                  />
                  <AddButton
                    minimal={false}
                    onClick={() => {
                      setBaseData(ctx.formik.values);
                      add(false);
                    }}
                    text={t(ETLCodes.General_Add)}
                    intent={Intent.PRIMARY}
                  />
                </ButtonsBloc>
              </InlineButtonContainer>
            )}
          </FGCustomPanel>
        </FieldGroup>
      </SmallFormGenerator>
      <FieldSet title={t(ETLCodes.TableResults, { count: totalCount })}>
        <DataTable dateFormat="dd-MM-yyyy" tableState={tableState} loading={loading} columns={columns} />
      </FieldSet>
    </FieldSet>
  );
};
